import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_ITM_LIST_SRCH, SG_SURV_ITM_LIST } from "./services/PtliSurvItmMgmtService";
import { PTLI0419001Q } from "../ques/PTLI0419001Q";
import { PTLI0418005Q } from "../qustnr/PTLI0418005Q";

/*
 * Survey Item Management List
 * !== 설문 항목 관리 목록 ==!
 */

export const PTLI0420001Q = (props: any): JSX.Element => {
    const { callback } = props;
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" }, // 업무구분
            { area: "comnCd", comnCd: "COM0052" }, // 포털유형
            { area: "comnCd", comnCd: "PTL0061" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survItmListSrch: useForm({
            defaultSchema: SF_SURV_ITM_LIST_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survItmList: useGrid({
            defaultSchema: SG_SURV_ITM_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvItmList: useFetch({
            api: (page = grid.survItmList.page) =>
                APIS.getSurvItmList(form.survItmListSrch.getValues(), page, grid.survItmList.size),
            enabled: comnUtils.isEmpty(form.survItmListSrch.errors),
            key: [grid.survItmList.page, grid.survItmList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.survItmListSrch.getValues(),
                    page: grid.survItmList.page,
                    size: grid.survItmList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    const render = {
        cell: {
            qustnrTtle: (props: any) => {
                const { rowValues, value } = props;
                if (callback) {
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                callback(rowValues);
                            }}
                        >
                            {value}
                        </a>
                    );
                } else {
                    const { rowValues, value } = props;
                    return <Link to={`${URLS.ptli0420004q}/${rowValues.quesItmId}`}>{value}</Link>;
                }
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchBtnClick: () => {
            grid.survItmList.setPage(0);
            fetch.getSurvItmList.fetch(0);
        },

        onNewBtnClick: () => navigate(URLS.ptli0420002s),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survItmListSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.survItmListSrch.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survItmListSrch.schema.qustnrTtle}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmListSrch.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survItmListSrch.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };


                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418005Q 
                                            callback={modalCallback}
                                            />,
                                        });
                                    },
                                }}
                            />
                            <Group.Control
                                {...form.survItmListSrch.schema.quesCn}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survItmListSrch.setValue("quesCn", item.quesCn);
                                                form.survItmListSrch.setValue("quesId", item.quesId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0419001Q callback={modalCallback} 
                                            />,
                                        });
                                    },
                                }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survItmListSrch.schema.itmCn} />
                            <Group.Control {...form.survItmListSrch.schema.rgsrUsr} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.survItmListSrch.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.survItmList.grid}
                            data={fetch.getSurvItmList.data?.survItmList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
